import React from 'react';
import { Autocomplete, CircularProgress, TextField } from '@mui/material';

import './SelectAutocomplete.css';
import { SelectAutocompleteProps } from './SelectAutocomplete.types';

/**
 * SelectAutocomplete Component
 * 
 * Autocomplete input with customizable options and styling.
 * Supports both free-form input and select-only modes.
 * Features custom styling with brand colors and responsive behavior.
 * 

 * <SelectAutocomplete
 *   options={['Option 1', 'Option 2', 'Option 3']}
 *   value={selected}
 *   onChange={(value) => setSelected(value)}
 *   placeholder="Select an option"
 *   selectOnly={true}
 * />
 */
export const SelectAutocomplete: React.FC<SelectAutocompleteProps> = ({
  options,
  value,
  placeholder = '',
  disabled = false,
  className = '',
  selectOnly = false,
  showClearButton = false,
  loading = false,
  onClear,
  onChange
}) => {
  const [localInputValue, setLocalInputValue] = React.useState('');

  return (
    <Autocomplete
      options={options}
      loading={loading}
      value={selectOnly ? (options.length > 0 && value ? value : null) : showClearButton ? '' : null}
      inputValue={selectOnly ? undefined : localInputValue}
      onInputChange={
        selectOnly
          ? undefined
          : (_, newInputValue) => {
              setLocalInputValue(newInputValue);
            }
      }
      onChange={(_, newValue) => {
        if (newValue === null && showClearButton && onClear) {
          onClear();
        } else if (newValue) {
          onChange(newValue);
        }
        if (!selectOnly) {
          setLocalInputValue('');
        }
      }}
      disabled={disabled}
      className={`select-autocomplete-root ${className}`}
      size="small"
      freeSolo={!selectOnly}
      disableClearable={!showClearButton}
      disablePortal={selectOnly}
      componentsProps={{
        paper: {
          className: 'select-autocomplete-paper-default'
        }
      }}
      renderInput={params => (
        <TextField
          {...params}
          placeholder={placeholder}
          variant="outlined"
          size="small"
          inputProps={{
            ...params.inputProps,
            readOnly: selectOnly,
            className: selectOnly ? 'select-only' : ''
          }}
          InputProps={{
            ...params.InputProps,
            className: 'select-autocomplete-input',
            endAdornment: (
              <>
                {loading ? <CircularProgress color="inherit" size={18} /> : null}
                {params.InputProps.endAdornment}
              </>
            )
          }}
        />
      )}
    />
  );
};
